<template>
  <div
      class="chart"
      :style="{ flexDirection: legend === 'Top' ? 'column-reverse' : 'column' }"
      ref="accessor"
  >
    <div
        class="chart-content"
        ref="chartRef"
        :class="{'chart-content-pie': props.type === 'pie', 'waterfall' : props.waterfall}"
        :style="{
          flexDirection: setPieLegend
        }"
    ></div>
    <div class="legends" v-if="legend">
      <div
          class="legend"
          v-for="(legend, index) in legends"
          :key="index"
          :style="{ color: fill }"
      >
        <div class="block" :style="{backgroundColor: themeColors[index] }"></div>
        {{ legend }}
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, inject, nextTick, onMounted, PropType, ref, watch} from 'vue'
import tinycolor from 'tinycolor2'
import {BarChart, LineChart, PieChart} from 'chartist'
import {ChartData, ChartOptions, ChartType} from '@/types/slides'
import {injectKeySlideScale} from '@/types/injectKey'
import ChartistTooltip from "chartist-plugin-tooltips-updated";
import 'chartist-plugin-tooltips-updated/dist/chartist-plugin-tooltip.css'
import ChartistPlugin from "chartist-plugin-legend"

import 'chartist/dist/index.css'
import {useGivingTreeStore, useSlidesStore} from "@/store";
import {storeToRefs} from "pinia";
import useComputedTreeValue from "@/views/components/element/hooks/useComputedTreeValue";

const accessor = ref()
const fontSize = computed(() => {
  return props.labelStyle?.fontSize
})

const barThickness = computed(() => props.labelStyle?.barThickness)

const fill = computed(() => {
  return props.labelStyle?.fill
})

const props = defineProps({
  width: {
    type: Number,
    required: true,
  },
  height: {
    type: Number,
    required: true,
  },
  type: {
    type: String as PropType<ChartType>,
    required: true,
  },
  data: {
    type: Object as PropType<ChartData>,
    required: true,
  },
  options: {
    type: Object as PropType<ChartOptions>,
  },
  themeColor: {
    type: Array as PropType<string[]>,
    required: true,
  },
  legends: {
    type: Array as PropType<string[]>,
    required: true,
  },
  gridColor: {
    type: String,
  },
  legend: {
    type: String as PropType<'Top' | 'Bottom'>,
  },
  labelStyle: {
    type: Object
  },
  compact: {
    type: Boolean
  },
  id: {
    type: String
  },
  yAxis: {
    type: Boolean
  },
  pieLegend: {
    type: String as PropType<'Left' | 'Right' | 'None'>
  },
  waterfall: {
    type: Boolean
  }
})

const chartRef = ref<HTMLElement>()
const slideScale = inject(injectKeySlideScale) || ref(1)

const givingTreeStore = useGivingTreeStore()
const slidesStore = useSlidesStore()

const {valueDriversByHookId, updated} = storeToRefs(givingTreeStore)

let chart: LineChart | BarChart | PieChart | undefined

const {getActiveHookId, getComputedValue, getComputedType, getComputedPrecision} = useComputedTreeValue()

onMounted(() => {
  updateTheme()
  updateGridColor()
  renderChart()
})


const chartHeight = computed(() => props.labelStyle?.chartHeight ? props.labelStyle?.chartHeight : `${props.height}px`)
const chartWidth = computed(() => props.labelStyle?.chartWidth ? props.labelStyle?.chartWidth : `${props.width}px`)

const getPieChartData = () => {
  return {...props.data, series: props.data.series[0]}
}

const setPieLegend = computed(() => {
  let direction: string;
  if (props.pieLegend === 'Left') {
    direction = 'row'
  } else if (props.pieLegend === 'Right') {
    direction = 'row-reverse'
  } else {
    direction = ''
  }
  return direction
})

const displayLegends = ref('block')
watch(setPieLegend, (newVal) => {
  if (newVal === '') displayLegends.value = 'none'
  else displayLegends.value = 'block'
})

const getOptions = () => {
  const propsOptions = props.options || {}

  let pointClass = 'ct-point';


  if (props.type === 'bar') {
    pointClass = 'ct-bar';
  } else if (props.type === 'pie') {
    if (propsOptions.donut) {
      pointClass = 'ct-slice-donut';
    } else {
      pointClass = 'ct-slice-pie';
    }
  }

  const legendPlugin = ChartistPlugin({ legendNames: props.data.labels })

  setupChartPadding()

  return {
    ...propsOptions,
    width: chartWidth.value,
    height: chartHeight.value,
    chartPadding: props.options.chartPadding,
    showLabel: false,
    responsive: false,
    axisY: {
      scaleMinSpace: 50,
      labelInterpolationFnc: function(value) {
        if (value === 0) {
          return '';
        }
        return format(value);
      }
    },
    plugins: [
      ChartistTooltip({
        appendToBody: true,
        pointClass: pointClass,
        tooltipFnc: function(meta, value) {
          return setPrecisionValue(meta, value)
        }
      }),
      props.type === 'pie' ? legendPlugin : undefined
    ].filter(Boolean)
  };

}

const escapingMap = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#039;"
};

const setupChartPadding = () => {
  if (!props.options.chartPadding) {
    props.options.chartPadding = props.type === 'pie' ? 20 : { top: 0, right: 0, bottom: 50, left: 10 };
  } else if (props.type === 'pie' && typeof props.options.chartPadding === 'object') {
    props.options.chartPadding = 20;
  } else if (props.type !== 'pie' && typeof props.options.chartPadding === 'number') {
    props.options.chartPadding = { top: 0, right: 0, bottom: 50, left: 10 };
  }
}

function deserialize(data) {
  if (typeof data !== "string") {
    return data;
  }
  if (data === "NaN") {
    return NaN;
  }
  data = Object.keys(escapingMap).reduce((result, key)=>result.replaceAll(escapingMap[key], key)
      , data);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let parsedData = data;
  if (typeof data === "string") {
    try {
      parsedData = JSON.parse(data);
      parsedData = parsedData.data !== undefined ? parsedData.data : parsedData;
    } catch (e) {
      /* Ingore */ }
  }
  return parsedData;
}

const renderChart = () => {
  //TODO: Handle multiple charts on a slide
  if (!chartRef.value) return

  const options = getOptions()

  if (props.type === 'bar') {
    chart = new BarChart(chartRef.value, getChartData(), options)
  }
  if (props.type === 'line') chart = new LineChart(chartRef.value, props.data, options)
  if (props.type === 'pie') {
    const valueDrivers = valueDriversByHookId.value;
    const valueDriverHookIds = Object.keys(valueDrivers);

    if (props.pieLegend === 'None') displayLegends.value = 'none'

    props.data.labels = []

    valueDriverHookIds.forEach((hookId) => {
      const valueDriver = valueDrivers[hookId];
      // TODO: what is unitValue vs value

      if (valueDriver.value !== 0 && valueDriver.enabled && valueDriver.value > 0) {
        props.data.labels.push(valueDriver.name);
      }
    });


    const series = valueDriverHookIds.map((hookId) => {
      const valueDriver = valueDrivers[hookId];
      if (valueDriver.value !== 0 && valueDriver.enabled && valueDriver.value > 0) {
        getActiveHookId(hookId)
        const name = valueDrivers[hookId].name;
        //hardcoding to 0 index because we will only have 1 series per chart
        props.data.hookIds[0].push(hookId);

        return {meta: {name: `${name}:`, hookId: hookId}, value: getComputedValue.value}
      }

    }).filter(item => item !== undefined);


    props.data.series[0].splice(0, series.length, ...series);

    chart = new PieChart(chartRef.value, getPieChartData(), options)
    slidesStore.updateElement({id: props.id, props: {data: props.data} })

  }
}

// Watch giving tree to see when it's getting updated to
// re-render the chart with the new values
watch(updated, (newValue) => {
  if (newValue) {
    renderChart()
    nextTick(() => {
      updated.value = false
    })
  }
});

const getChartData = () => {

  const data = JSON.parse(JSON.stringify(props.data))

  const inivisibleSeries: any = []

  data.series.forEach(series => {
    let runningTotal = 0

    series.forEach(item => {
      if (item.meta.hookId) {
        getActiveHookId(item.meta.hookId)

        let isValueDriver = !!givingTreeStore.valueDriversByHookId[item.meta.hookId]

        const spacer = isValueDriver ? runningTotal : 0

        item.value = getComputedValue.value
        runningTotal += item.value

        inivisibleSeries.push({
          value: spacer,
          meta: {name: item.meta.name, hookId: item.meta.hookId}
        })
      }
    })
  })

  // if waterfall is checked - we need an invisible series at slot 0


  if (props.waterfall) {

    data.series.unshift(inivisibleSeries)

    setValueToYouOurPrice(data)

  }
  return data
}

const setValueToYouOurPrice = (data) => {
  const nonInvisibleSeries = data.series[1]

  const includesOurPrice = nonInvisibleSeries.find(item => {
    return item.meta.hookId === 'ourPrice'
  })

  if (!includesOurPrice) {
    return;
  }

  if (includesOurPrice) {
    const valueToCustomerIndex = nonInvisibleSeries.findIndex(item => item.meta.hookId === 'valueToCustomer');
    const ourPriceIndex = nonInvisibleSeries.findIndex(item => item.meta.hookId === 'ourPrice');

    const newSeries = Array(nonInvisibleSeries.length).fill(0);

    if (valueToCustomerIndex !== -1) {
      const ourPriceValue = nonInvisibleSeries.find(item => item.meta.hookId === 'ourPrice').value


      newSeries[valueToCustomerIndex] = {
        value: ourPriceValue,
        meta: {name: 'Our Price', hookId: 'ourPrice'}
      }
    }

    if (ourPriceIndex !== -1) {
      nonInvisibleSeries.splice(ourPriceIndex, 1);
      newSeries.splice(ourPriceIndex, 1)
      data.series[0].splice(ourPriceIndex, 1)
      data.labels.splice(ourPriceIndex, 1)
    }

    data.series.push(newSeries)
  }
}


const updateChart = () => {
  if (!chart) {
    renderChart()
    return
  }

  const options = getOptions()
  const data = props.type === 'pie' ? getPieChartData() : getChartData()
  chart.update(data, options)

  applyLabelStyles()
}

watch(() => props.options, (newVal, oldVal) => {
  if (newVal.donut !== oldVal.donut) {
    renderChart()
  }

  if (newVal?.stackBars !== oldVal?.stackBars && newVal?.stackBars) renderChart()

  renderChart()
}, { deep: true })

watch([
  () => props.width,
  () => props.height,
  () => props.data,
  () => props.options,
  () => props.waterfall,
  () => props.labelStyle,
  slideScale,
], updateChart)

watch(() => props.type, () => {
  if (props.type !== 'pie') displayLegends.value = 'none'
  else displayLegends.value = 'block'
  renderChart()
})

const applyLabelStyles = () => {
  nextTick(() => {
    if (!accessor.value) {
      return
    }
    setTimeout(() => {
      const labels = accessor.value.querySelectorAll('.ct-label');
      if (labels) {
        labels.forEach((label) => {
          label.style.fontSize = props.labelStyle?.fontSize;
          label.style.fill = props.labelStyle?.fill;
          label.style.color = props.labelStyle?.fill;

        });
      }
    }, 100)
  })
};

const currencyFormat = () => {
  const value = getComputedValue.value ? getComputedValue.value : 0

  // const maxDecimalPlaces = value >= 1000000 ? 1 : 0
  // maximumFractionDigits: maxDecimalPlaces,

  return format(value)
}

const format = (value) => {
  //TODO: use currency and locale off giving tree
  // this seems to work fine - we might want to check props.data for currency in the future

  const valueType = getComputedType.value

  if (valueType === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: props.compact && value >= 10000 ? 'compact': 'standard',
      compactDisplay: 'short'
    }).format(value);
  }

  if (valueType === 'percent') {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: getComputedPrecision.value,
      maximumFractionDigits: getComputedPrecision.value
    }).format(value) + '%';
  }

  return new Intl.NumberFormat('en-US', {
    notation: props.compact && value >= 10000 ? 'compact': 'standard',
  }).format(value);
}

const setPrecisionValue = (meta, value) => {
  const deserializedMeta = deserialize(meta)

  const {name, hookId} = deserializedMeta

  if (hookId === '') return `${name}: ${value}`

  getActiveHookId(hookId)

  const hasComputedValue = getComputedValue.value !== null;


  if (hasComputedValue && mode.value === 'currency') {
    const newVal = currencyFormat();
    return `${name} ${newVal}`
  }

  if (hasComputedValue) {
    const precision = getComputedPrecision.value || 0;
    return `${name} ${getComputedValue.value.toFixed(precision)}`
  }

  return 'Computed Value';
}


const mode = computed(() => {
  if (getComputedType.value === 'currency') {
    return 'currency'
  }
  return 'decimal'
})

const themeColors = computed(() => {
  let colors: string[] = []
  if (props.themeColor.length >= 10) colors = props.themeColor
  else if (props.themeColor.length === 1) colors = tinycolor(props.themeColor[0]).analogous(10).map(color => color.toHexString())
  else {
    const len = props.themeColor.length
    const supplement = tinycolor(props.themeColor[len - 1]).analogous(10 + 1 - len).map(color => color.toHexString())
    colors = [...props.themeColor.slice(0, len - 1), ...supplement]
  }
  return colors
})

// Update theme color：
// If the number of theme colors currently set is less than 10, the remaining part will get the color similar to the last theme color as the color matching
const updateTheme = () => {
  if (!chartRef.value) return
  let currentColor = 0

  if (props.waterfall) {
    chartRef.value.style.setProperty(`--theme-color-1`, 'transparent')

    for (let i = 0; i < 10; i++) {
      const hookId = props.data.series[0][i]?.meta?.hookId
      if (hookId && givingTreeStore.valueDriversByHookId[hookId]) {
        chartRef.value.style.setProperty(`--theme-color-${i + 2}`, 'green')

      }
      else {
        chartRef.value.style.setProperty(`--theme-color-${i + 2}`, themeColors.value[currentColor])
        currentColor++
      }
    }
  }
  else {
    for (let i = 0; i < 10; i++) {
      chartRef.value.style.setProperty(`--theme-color-${i + 1}`, themeColors.value[i])
    }
  }
}

watch(themeColors, updateTheme)

// Update the grid color, including the text part of the coordinates
const updateGridColor = () => {
  if (!chartRef.value) return
  if (props.gridColor) chartRef.value.style.setProperty(`--grid-color`, props.gridColor)
}

watch(() => props.gridColor, updateGridColor)
</script>


<style lang="scss" scoped>
.chart {
  display: flex;
}

.chart-content {
  transform-origin: 0 0;
}

:deep(.ct-label) {
  font-size: v-bind(fontSize) !important;
  fill: v-bind(fill) !important;
  color: v-bind(fill) !important;
}

:deep(.ct-bar) {
  stroke-width: v-bind(barThickness);
}

@mixin waterfall($ct-series-names) {
  @for $i from 2 to length($ct-series-names) {
    $color: var(--theme-color-#{$i});

    @for $j from 1 to 10 {
      .ct-series-#{nth($ct-series-names, $i)} .ct-bar:nth-child(#{$j}) {
        stroke: var(--theme-color-#{$j+1});
      }

      .ct-series-b .ct-bar:last-child {
        stroke: var(--theme-color-#{$j - 1})
      }
    }
  }
}

:deep(.chart-content) {
  $ct-series-names: (a, b, c, d, e, f, g, h, i, j);

  --theme-color-1: #666;
  --theme-color-2: #666;
  --theme-color-3: #666;
  --theme-color-4: #666;
  --theme-color-5: #666;
  --theme-color-6: #666;
  --theme-color-7: #666;
  --theme-color-8: #666;
  --theme-color-9: #666;
  --theme-color-10: #666;

  @for $i from 1 to length($ct-series-names) {
    $color: var(--theme-color-#{$i});


    .ct-series-#{nth($ct-series-names, $i)} .ct-point {
      stroke: $color;
    }
    .ct-series-#{nth($ct-series-names, $i)} .ct-area {
      fill: $color;
    }
    .ct-series-#{nth($ct-series-names, $i)} .ct-bar {
      stroke: $color;
    }

    .ct-series-#{nth($ct-series-names, $i)} .ct-line {
      stroke: $color;
    }

    .ct-series-#{nth($ct-series-names, $i)} .ct-slice-donut {
      stroke: $color;

      &:hover {
        opacity: 0.7;
      }
    }
    .ct-series-#{nth($ct-series-names, $i)} .ct-slice-pie {
      fill: $color;

      &:hover {
        opacity: 0.7;
      }
    }

    .ct-legend .ct-series-#{$i - 1}:before {
      background-color: $color;
      border-color: $color;
    }

    .ct-chart-line-multipleseries .ct-legend .ct-series-#{$i - 1}:before {
      background-color: $color;
      border-color: $color;
    }
  }

  .ct-legend li.inactive:before {
    background: transparent;
  }

  .ct-chart-line-multipleseries .ct-legend li.inactive:before {
    background: transparent;
  }

  --grid-color: rgba(0, 0, 0, 0.4);

  .ct-grid {
    stroke: var(--grid-color);
  }

  &.waterfall {
    @include waterfall($ct-series-names);
  }
}

:deep(.chart-content-pie) {
  display: flex;
  justify-content: center;
}

:deep(.legends) {
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
}

:deep(.legend) {
  display: flex;
  align-items: center;

  & + .legend {
    margin-left: 10px;
  }

  .block {
    width: 10px;
    height: 10px;
    margin-right: 5px;
  }
}

:deep(.chartist-tooltip) {
  position: absolute;
  display: inline-block;
  opacity: 0;
  min-width: 5em;
  padding: 0.5em;
  background: #f0f0f0;
  color: #495057;
  font-family: inherit;
  font-weight: 400;
  text-align: center;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  pointer-events: none;
  z-index: 1;
  transition: opacity 0.2s ease-in-out;
}

:deep(.chartist-tooltip:before) {
  content: "";
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 8px;
  border-style: solid;
  border-color: transparent transparent #f0f0f0;
}

:deep(.chartist-tooltip.tooltip-show) {
  opacity: 1;
}

:deep(.ct-legend) {
  list-style: none;
  margin-top: 20px !important;
  margin-left: 20px;
  font-size: v-bind(fontSize);
  flex: 1;
  display: v-bind(displayLegends);
}

:deep(.ct-legend li) {
  position: relative;
  padding-left: 23px;
  margin-right: 10px;
  margin-bottom: 3px;
  cursor: pointer;
  display: inline-block;
  color: v-bind(fill);
}
:deep(.ct-legend li:before) {
  width: 12px;
  height: 12px;
  position: absolute;
  left: 0;
  top: 5px;
  content: '';
  border: 3px solid transparent;
  border-radius: 2px;
}


:deep(.ct-hidden) {
  opacity: 0;
}

:deep(.ct-dimmed) {
  opacity: 0.5;
}

:deep(.ct-grid) {
  stroke-dasharray: none;
}
</style>
